import { useState } from 'react'
import { isBatchExpiredOrExpiring } from '../utils/batchUtils'
import { Batch } from '../../../interfaces/Batch'
import { eTypePosition } from '../../../enums/eTypePosition'

interface UseQuantityValidationProps {
  sharedState: {
    selectedBatch?: Batch
    availableStock?: number
    originZone?: any
    originPositionType?: number
    cantidad?: number | null
    expirationDays?: number
  }
  setSharedState: (updater: (prev: any) => any) => void
  onQuantityValidated: () => void
  onShowMermaQuestion: () => void
  onAutoSendToQuality: (isDamaged: boolean) => Promise<void>
}

interface UseQuantityValidationReturn {
  localQuantity: string
  setLocalQuantity: (value: string) => void
  quantityValidated: boolean
  setQuantityValidated: (value: boolean) => void
  error: string
  setError: (value: string) => void
  handleQuantityChange: (value: string) => void
  handleValidateQuantity: () => void
  handleEditQuantity: () => void
}

export const useQuantityValidation = ({
  sharedState,
  setSharedState,
  onQuantityValidated,
  onShowMermaQuestion,
  onAutoSendToQuality
}: UseQuantityValidationProps): UseQuantityValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde ConfirmationStep)
  const [localQuantity, setLocalQuantity] = useState(sharedState.cantidad ? String(sharedState.cantidad) : '')
  const [quantityValidated, setQuantityValidated] = useState(Boolean(sharedState.cantidad))
  const [error, setError] = useState('')

  const handleQuantityChange = (value: string) => {
    setLocalQuantity(value)
    setQuantityValidated(false)
    setError('')
  }

  const handleValidateQuantity = async () => {
    const numValue = parseInt(localQuantity) || 0

    // Determinar stock máximo disponible según FEFO
    const maxQuantity = sharedState.selectedBatch
      ? sharedState.selectedBatch.quantity
      : sharedState.availableStock

    if (numValue > 0 && numValue <= (maxQuantity || 0)) {
      setSharedState(prev => ({
        ...prev,
        cantidad: numValue
      }))
      setQuantityValidated(true)
      setError('')

      // Verificar si lote está vencido o por vencer
      const shouldAutoSendToQuality = isBatchExpiredOrExpiring(
        sharedState.selectedBatch,
        sharedState.expirationDays || 0
      )

      if (shouldAutoSendToQuality) {
        // Auto-enviar a calidad sin preguntar
        await onAutoSendToQuality(true)
        return
      }

      // Mostrar pregunta de merma solo si la posición de origen es de tipo Venta y tiene zona
      if (
        sharedState.originPositionType === eTypePosition.Venta &&
        sharedState.originZone !== null &&
        sharedState.originZone !== undefined
      ) {
        onShowMermaQuestion()
        return
      } else {
        // Si no tiene zona, continuar con el flujo normal
        onQuantityValidated()
      }
    } else if (numValue > (maxQuantity || 0)) {
      const context = sharedState.selectedBatch ? 'del lote seleccionado' : 'disponible'
      setError(`Cantidad no puede ser mayor a ${maxQuantity} ${context}`)
      setQuantityValidated(false)
    } else {
      setError('Ingrese una cantidad válida mayor a 0')
      setQuantityValidated(false)
    }
  }

  const handleEditQuantity = () => {
    setQuantityValidated(false)
    setSharedState(prev => ({ ...prev, cantidad: null }))
    setError('')
  }

  return {
    localQuantity,
    setLocalQuantity,
    quantityValidated,
    setQuantityValidated,
    error,
    setError,
    handleQuantityChange,
    handleValidateQuantity,
    handleEditQuantity
  }
}